// JD Application Details View Component
import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
    Box,
    Typography,
    Alert,
    CircularProgress,
    Button,
    Paper,
    Grid,
    Card,
    CardContent,
    Divider,
    Chip,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    IconButton,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Avatar,
    Breadcrumbs,
    Link,
    Stack,
    Container
} from '@mui/material';
import {
    ArrowBack as ArrowBackIcon,
    Print as PrintIcon,
    Download as DownloadIcon,
    Visibility as VisibilityIcon,
    Business as BusinessIcon,
    LocationOn as LocationOnIcon,
    Person as PersonIcon,
     History as HistoryIcon,
    Assignment as AssignmentIcon,
    Science as ScienceIcon,
    Assessment as AssessmentIcon,
    CheckCircle as CheckCircleIcon,
    Dashboard as DashboardIcon
} from '@mui/icons-material';
import CEOApiService from '../../../services/CEOApiService';
import { decryptAppId, decryptText } from '../../../utils/encryption';

// Helper functions
const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'N/A';

    const options = {
        year: 'numeric',
        month: 'long',
        day: '2-digit',
    };
    return date.toLocaleDateString('en-US', options);
};



const JDApplicationView = () => {
    const location = useLocation();
    const navigate = useNavigate();

    // State management
    const [applicationData, setApplicationData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [disciplineData, setDisciplineData] = useState([]);
    const [groupData, setGroupData] = useState([]);
    const [assessorsData, setAssessorsData] = useState([]);

    // Dialog states
    const [disciplineDialogOpen, setDisciplineDialogOpen] = useState(false);
    const [groupDialogOpen, setGroupDialogOpen] = useState(false);
    const [assessorsDialogOpen, setAssessorsDialogOpen] = useState(false);

    // Get user data
    const userData = JSON.parse(localStorage.getItem('userData') || '{}');
    const orgId = userData.orgId;
    const userTypeId = userData.userTypeId;

    // Extract parameters from URL
    const urlParams = new URLSearchParams(location.search);
    const encryptedAppId = urlParams.get('appId');
    const encryptedOrgId = urlParams.get('orgId');
    const labTypeId = urlParams.get('labTypeId');
    const handleClick = () => {
        navigate(
            '/main/ceo/applications/appdashboard?appId=' + encodeURIComponent(encryptedAppId) +
            (encryptedOrgId ? '&orgId=' + encodeURIComponent(encryptedOrgId) : '') +
            (labTypeId ? '&labTypeId=' + encodeURIComponent(labTypeId) : '')
        );
    };
       // View application history
        const viewHistory = () => {
            try {
                navigate(
            '/main/ceo/applications/history?appId=' + encodeURIComponent(encryptedAppId));
            } catch (err) {
                console.error('Navigate to history error:', err);
            }
        };

    // Load application details - matching ASP.NET rptrApps_DataBind logic
    const loadApplicationDetails = async () => {
        try {
            // Session validation - matching ASP.NET Page_Load
            if (!userData.orgId || !localStorage.getItem('authToken')) {
                setError('Session expired. Please login again.');
                return;
            }

            if (!encryptedAppId) {
                throw new Error('Application ID is required');
            }

            setLoading(true);
            setError(null);

            // Decrypt the application ID
            const appId = decryptAppId(encryptedAppId);
            const applicationOrgId = encryptedOrgId ? decryptText(encryptedOrgId) : orgId;
            // Call JD API matching ASP.NET LabApplications_Acts stored procedure with mode 52
            const data = await CEOApiService.getApplicationDetails({
                appId,
                orgId: applicationOrgId,
            });
            if (data.data.success && data.data.data) {
                // Handle both single object and array responses
                let applicationInfo = data.data.data;
                if (Array.isArray(applicationInfo)) {
                    applicationInfo = applicationInfo[0]; // Take first record
                }

                setApplicationData(applicationInfo);

                // Load additional status information matching ASP.NET statusCAB method
                await loadApplicationStatus(appId);
            } else {
                throw new Error(data.message || 'Failed to load application details');
            }
        } catch (err) {
            console.error('Load application details error:', err);
            setError(`Error loading application details: ${err.message}`);
        } finally {
            setLoading(false);
        }
    };

    // Load application status - matching ASP.NET statusCAB method
    const loadApplicationStatus = async (appId) => {
        try {
            const statusData = await CEOApiService.getApplicationStatus({ appId });
            if (statusData.success && statusData.data) {
                // Merge status information with application data
                setApplicationData(prevData => ({
                    ...prevData,
                    ...statusData.data
                }));
            }
        } catch (err) {
            console.error('Load application status error:', err);
            // Don't throw error here as it's supplementary data
        }
    };

    // Load disciplines
    const loadDisciplines = async (appId, labTypeId) => {
        try {
            const data = await CEOApiService.getDisciplines({ appId, labTypeId });
            if (data.success) {
                setDisciplineData(data.data || []);
                setDisciplineDialogOpen(true);
            }
        } catch (err) {
            console.error('Load disciplines error:', err);
        }
    };

    // Load groups
    const loadGroups = async (appId, labTypeId) => {
        try {
            const data = await CEOApiService.getGroups({ appId, labTypeId });
            if (data.success) {
                setGroupData(data.data || []);
                setGroupDialogOpen(true);
            }
        } catch (err) {
            console.error('Load groups error:', err);
        }
    };

    // Load assessors
    const loadAssessors = async (appId) => {
        try {
            const data = await CEOApiService.getAssessors({ appId });
            if (data.success) {
                let assessors = [];
                if (data.data && data.data.data) {
                    assessors = Array.isArray(data.data.data) ? data.data.data : [];
                } else if (data.data) {
                    assessors = Array.isArray(data.data) ? data.data : [];
                }
                setAssessorsData(assessors);
                setAssessorsDialogOpen(true);
            }
        } catch (err) {
            console.error('Load assessors error:', err);
        }
    };

    // Handle back navigation
    const handleBack = () => {
        navigate(-1);
    };


    // Initial load
    useEffect(() => {
        loadApplicationDetails();
    }, [encryptedAppId, encryptedOrgId]);

    // Loading state
    if (loading) {
        return (
            <Box sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                minHeight: '60vh'
            }}>
                <CircularProgress size={60} />
            </Box>
        );
    }

    // Error state
    if (error) {
        return (
            <Box sx={{ p: 3 }}>
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
                <Button variant="outlined" onClick={handleBack} startIcon={<ArrowBackIcon />}>
                    Go Back
                </Button>
            </Box>
        );
    }

    // No data state
    if (!applicationData) {
        return (
            <Box sx={{ p: 3 }}>
                <Alert severity="warning" sx={{ mb: 2 }}>
                    No application data found
                </Alert>
                <Button variant="outlined" onClick={handleBack} startIcon={<ArrowBackIcon />}>
                    Go Back
                </Button>
            </Box>
        );
    }

    return (
        <Box sx={{ minHeight: '100vh', backgroundColor: '#f8fafc' }}>
            {/* Main Content */}
            <Container maxWidth="xl">
                <Card 
                    sx={{ 
                        boxShadow: '0 8px 32px rgba(0,0,0,0.12)', 
                        borderRadius: 3,
                        border: '1px solid rgba(0,0,0,0.08)',
                        overflow: 'hidden'
                    }}
                >
                    <CardContent sx={{ p: 0 }}>
                        {/* Table Header */}
                        <Box 
                            sx={{ 
                                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                color: 'white',
                                p: 3,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between'
                            }}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <Avatar
                                    sx={{
                                        bgcolor: 'rgba(255,255,255,0.2)',
                                        mr: 2,
                                        width: 48,
                                        height: 48,
                                    }}
                                >
                                    <BusinessIcon />
                                </Avatar>
                                <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                                    Application Details
                                </Typography>
                                
                            </Box>
                              <Stack direction="row" spacing={2}>
                                <Button
                                    variant="contained"
                                    startIcon={<ArrowBackIcon />}
                                    onClick={handleBack}
                                    sx={{
                                        bgcolor: 'rgba(255,255,255,0.2)',
                                        '&:hover': {
                                            bgcolor: 'rgba(255,255,255,0.3)',
                                        },
                                    }}
                                >
                                    Back
                                </Button>
                            </Stack>
                        </Box>

                        {/* Enhanced Table */}
                        <TableContainer>
                            <Table sx={{ minWidth: 650 }}>
                                <TableHead>
                                    <TableRow 
                                        sx={{ 
                                            background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
                                        }}
                                    >
                                        <TableCell 
                                            sx={{ 
                                                fontWeight: 'bold', 
                                                fontSize: '1rem',
                                                color: 'primary.main',
                                                py: 2.5,
                                                borderBottom: '2px solid',
                                                borderColor: 'primary.main'
                                            }}
                                        >
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <AssignmentIcon fontSize="small" />
                                                Lab ID
                                            </Box>
                                        </TableCell>
                                        <TableCell 
                                            sx={{ 
                                                fontWeight: 'bold', 
                                                fontSize: '1rem',
                                                color: 'primary.main',
                                                py: 2.5,
                                                borderBottom: '2px solid',
                                                borderColor: 'primary.main'
                                            }}
                                        >
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <ScienceIcon fontSize="small" />
                                                Application Type
                                            </Box>
                                        </TableCell>
                                        <TableCell 
                                            sx={{ 
                                                fontWeight: 'bold', 
                                                fontSize: '1rem',
                                                color: 'primary.main',
                                                py: 2.5,
                                                borderBottom: '2px solid',
                                                borderColor: 'primary.main'
                                            }}
                                        >
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <CheckCircleIcon fontSize="small" />
                                                Status
                                            </Box>
                                        </TableCell>
                                        <TableCell 
                                            sx={{ 
                                                fontWeight: 'bold', 
                                                fontSize: '1rem',
                                                color: 'primary.main',
                                                py: 2.5,
                                                borderBottom: '2px solid',
                                                borderColor: 'primary.main'
                                            }}
                                        >
                                            Date Created
                                        </TableCell>
                                        <TableCell 
                                            sx={{ 
                                                fontWeight: 'bold', 
                                                fontSize: '1rem',
                                                color: 'primary.main',
                                                py: 2.5,
                                                borderBottom: '2px solid',
                                                borderColor: 'primary.main'
                                            }}
                                        >
                                            Date Submitted
                                        </TableCell>
                                        <TableCell 
                                            sx={{ 
                                                fontWeight: 'bold', 
                                                fontSize: '1rem',
                                                color: 'primary.main',
                                                py: 2.5,
                                                borderBottom: '2px solid',
                                                borderColor: 'primary.main',
                                                textAlign: 'center'
                                            }}
                                        >
                                            Actions
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    <TableRow 
                                        sx={{ 
                                            '&:hover': { 
                                                backgroundColor: 'rgba(103, 126, 234, 0.08)',
                                                transform: 'scale(1.001)',
                                                transition: 'all 0.2s ease'
                                            },
                                            '&:last-child td, &:last-child th': { border: 0 }
                                        }}
                                    >
                                        <TableCell sx={{ py: 3 }}>
                                            <Chip 
                                                label={applicationData.ApplicationNo || applicationData.applicationNo || 'N/A'}
                                                color="primary"
                                                variant="filled"
                                                sx={{ 
                                                    fontWeight: 'bold',
                                                    fontSize: '0.9rem',
                                                    px: 2,
                                                    py: 1
                                                }}
                                            />
                                        </TableCell>
                                        <TableCell sx={{ py: 3 }}>
                                            <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                                                {applicationData.Name || applicationData.assessmentType || 'N/A'}
                                            </Typography>
                                        </TableCell>
                                        <TableCell sx={{ py: 3 }}>
                                            <Chip
                                                label={applicationData.Status || applicationData.status || 'N/A'}
                                                color="success"
                                                variant="outlined"
                                                sx={{ 
                                                    fontWeight: 'bold',
                                                    fontSize: '0.85rem',
                                                    borderWidth: 2,
                                                    height: 'auto',
                                                    minHeight: '32px',
                                                    '& .MuiChip-label': {
                                                        whiteSpace: 'normal',
                                                        textOverflow: 'clip',
                                                        overflow: 'visible',
                                                        display: 'block',
                                                        padding: '8px 12px',
                                                        lineHeight: 1.2
                                                    },
                                                    '&:hover': {
                                                        transform: 'scale(1.05)'
                                                    },
                                                    transition: 'transform 0.2s ease'
                                                }}
                                            />
                                        </TableCell>
                                        <TableCell sx={{ py: 3 }}>
                                            <Typography variant="body2" sx={{ fontWeight: 'medium', color: 'text.secondary' }}>
                                                {formatDate(applicationData.InsertDate)}
                                            </Typography>
                                        </TableCell>
                                        <TableCell sx={{ py: 3 }}>
                                            <Typography variant="body2" sx={{ fontWeight: 'medium', color: 'text.secondary' }}>
                                                {formatDate(applicationData.SubmitDate)}
                                            </Typography>
                                        </TableCell>
                                        <TableCell sx={{ py: 3, textAlign: 'center' }}>
                                            <Stack spacing={1} alignItems="center">
                                                <Button
                                                    variant="contained"
                                                    size="small"
                                                    onClick={handleClick}
                                                    startIcon={<DashboardIcon />}
                                                    sx={{
                                                        minWidth: 120,
                                                        '&:hover': {
                                                            transform: 'translateY(-2px)',
                                                            boxShadow: '0 6px 20px rgba(103, 126, 234, 0.4)'
                                                        },
                                                        transition: 'all 0.3s ease'
                                                    }}
                                                >
                                                    Dashboard
                                                </Button>
                                                <Button
                                                    variant="outlined"
                                                    size="small"
                                                    onClick={viewHistory}
                                                    startIcon={<HistoryIcon />}
                                                    sx={{
                                                        minWidth: 120,
                                                        '&:hover': {
                                                            transform: 'translateY(-2px)',
                                                            boxShadow: '0 6px 20px rgba(0,0,0,0.15)'
                                                        },
                                                        transition: 'all 0.3s ease'
                                                    }}
                                                >
                                                    History
                                                </Button>
                                            </Stack>
                                        </TableCell>
                                    </TableRow>
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </CardContent>
                </Card>
            </Container>

        </Box>
    );
};

export default JDApplicationView;